import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { USE_CASE_MAP } from "@/data/useCases";
import UploadDropzone from "@/components/UploadDropzone";
import { ArrowLeft, Check } from "lucide-react";

export default function UseCaseDetail() {
  const { key } = useParams();
  const navigate = useNavigate();
  const u = USE_CASE_MAP[key];

  const onFile = useCallback(
    (file) => {
      window.__PDF_REVIEW_PENDING_FILE__ = file;
      window.__PDF_REVIEW_PENDING_TYPE__ = key;
      navigate("/workspace");
    },
    [navigate, key]
  );

  if (!u) {
    return (
      <div className="max-w-[900px] mx-auto p-10">
        <div className="eyebrow">Not found</div>
        <div className="font-serif-display text-3xl mt-3">Playbook not found.</div>
      </div>
    );
  }

  return (
    <section
      data-testid={`use-case-detail-${key}`}
      className="max-w-[1200px] mx-auto px-6 md:px-10 py-14"
    >
      <button
        onClick={() => navigate("/use-cases")}
        className="btn-ghost focus-ring mb-6 -ml-2"
        data-testid="usecase-detail-back"
      >
        <ArrowLeft className="w-4 h-4" /> All playbooks
      </button>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 mb-4">
            <span className="font-mono-plex text-sm text-ink-50">{u.number}</span>
            <span className="pill pill-green" style={{ background: "#F0F7F3", color: "var(--forest)", borderColor: "#C8E6C9" }}>
              <span className="dot" />
              {u.tag}
            </span>
          </div>
          <h1 className="font-serif-display text-4xl md:text-5xl text-ink leading-tight">
            {u.label}
          </h1>
          <p className="mt-5 text-ink-70 leading-relaxed">
            <span className="text-ink font-medium">For: </span>
            {u.for_who}
          </p>

          <div className="mt-10">
            <div className="eyebrow mb-4">What we check</div>
            <ul className="space-y-3">
              {u.checks.map((c) => (
                <li key={c} className="flex items-start gap-3 text-sm text-ink-70">
                  <Check className="w-4 h-4 text-forest mt-0.5 flex-shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-10">
            <div className="eyebrow mb-4">Output</div>
            <p className="text-sm text-ink-70 leading-relaxed">{u.output}</p>
          </div>
        </div>

        <div className="md:col-span-7">
          <UploadDropzone
            onFile={onFile}
            testId={`usecase-dropzone-${key}`}
          />
          <p className="mt-4 text-xs text-ink-50 font-mono-plex uppercase tracking-widest">
            We'll pre-select {u.label} — you can still switch types.
          </p>
        </div>
      </div>
    </section>
  );
}
